import * as React from 'react';
import { createElement } from 'react';
import { Table as OriginalTable, Form, Field, ResponsiveGrid, Button, Dialog } from '@alifd/next';
import { formatFormItems } from '@/components/form/form/form';
import { nanoid } from 'nanoid';
const Table = (props, ref) => {
    const { children = [], columns, spacing = [0, 16, 16, 0], emptyContent, status, name: arrayName, size, __designMode, primaryKey = 'uid', field, isPreview: rootPreview, value = [], onChange: onTableFieldChange, ...otherProps } = props;
    const isPreview = rootPreview || status === 'readonly';
    const isDesign = __designMode === 'design';
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
    const dataSource = React.useMemo(() => value.map((item) => ({ ...item, uid: item.uid ?? nanoid() })), [value]);
    const formField = Field.useField({
        parseName: true,
    });
    const onRowChange = React.useCallback((selectedRowKeys, records) => {
        setSelectedRowKeys(selectedRowKeys);
        setSelectedRows(records);
        formField.setValues(records[0]);
    }, [formField]);
    const save = () => {
        formField.validate((errors, values) => {
            if (!errors) {
                const currentPrimaryKey = selectedRows?.[0]?.[primaryKey];
                if (currentPrimaryKey) {
                    const newData = dataSource.map((item) => {
                        if (item[primaryKey] === currentPrimaryKey) {
                            return values;
                        }
                        return item;
                    });
                    onTableFieldChange(newData);
                    resetSelectedRows();
                }
            }
        });
    };
    const resetSelectedRows = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };
    const cancle = () => {
        resetSelectedRows();
    };
    const rowSelection = {
        mode: 'single',
        onChange: onRowChange,
        selectedRowKeys,
        getProps: () => {
            return {
                disabled: isDesign || isPreview,
            };
        },
    };
    const Operation = React.useCallback(() => {
        const index = dataSource?.findIndex((d) => d[primaryKey] === selectedRowKeys[0]);
        const onDelete = () => {
            Dialog.confirm({
                v2: true,
                title: '确认删除吗',
                closeMode: ['close', 'esc', 'mask'],
                onOk: () => {
                    const newData = dataSource;
                    newData.splice(index, 1);
                    onTableFieldChange(newData);
                    resetSelectedRows();
                },
            });
        };
        const onAdd = () => {
            const newData = dataSource;
            newData.splice(index + 1, 0, { [primaryKey]: nanoid() });
            onTableFieldChange(newData);
        };
        return (createElement("span", null,
            createElement(Button, { type: "primary", onClick: onAdd, disabled: isDesign }, "\u65B0\u589E"),
            createElement(Button, { warning: true, style: { marginLeft: 4 }, onClick: onDelete, disabled: isDesign || selectedRowKeys.length === 0 }, "\u5220\u9664")));
    }, [dataSource, isDesign, onTableFieldChange, primaryKey, selectedRowKeys]);
    return (createElement("div", null,
        createElement("div", { style: { marginBottom: 4 } },
            createElement(Operation, null)),
        createElement(OriginalTable.StickyLock, { dataSource: dataSource, ref: ref, primaryKey: primaryKey, rowSelection: rowSelection, size: 'small', fixedHeader: true, isZebra: true, crossline: true, maxBodyHeight: 224 }, formatTableColumn(children)),
        (isDesign || selectedRows.length > 0) && (createElement(Form, { ...otherProps, isPreview: isPreview, field: formField, useLabelForErrorMessage: true },
            createElement(ResponsiveGrid, { gap: spacing, columns: columns }, children ? (formatFormItems(children, {
                ...otherProps,
                isPreview,
                componentProps: {
                    arrayEditFormField: formField,
                },
            })) : (createElement("div", { className: "empty-content" }, emptyContent))),
            isPreview ? null : (createElement("div", { style: { textAlign: 'center' } },
                createElement(Button.Group, null,
                    createElement(Button, { onClick: save, type: "primary", disabled: isDesign || selectedRows.length === 0 }, "\u4FEE\u6539"),
                    createElement(Button, { onClick: cancle, disabled: isDesign }, "\u53D6\u6D88"))))))));
};
const TableRef = React.forwardRef(Table);
export default TableRef;
const formatTableColumn = (children) => {
    const _children = children
        .filter((child) => child && ['function', 'object'].indexOf(typeof child) > -1)
        .filter((child) => child?.props?.formItemProps?.isShowInColumn);
    const columns = React.Children.map(_children, (child) => {
        const props = child.props;
        const { formItemProps = {} } = props;
        const { label, name } = formItemProps;
        return createElement(OriginalTable.Column, { title: label, dataIndex: name, width: 200 });
    });
    return columns;
};
